if (customElements && !customElements.get('vault-modal')) {
  class VaultModal extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-role="modal-trigger"]');

      this.modalWindow = null;
      this.buttonPrevStep = null;
      this.contentContainer = null;
      this.contentRoot = null;

      this.isEnableReleaseMode = this.dataset.isEnableReleaseMode === 'true';
      this.hasCustomerAccessTag = this.dataset.hasAccessTag === 'true';

      this.steps = {
        'collection-lock': null,
        'collection-unlock': null,
        'email-subscribe': null,
        'private-access': null,
        'countdown-new-drop': null
      };

      this.init();
    }

    get currentStep() {
      if (!this.contentRoot) {
        console.warn('Not found: contentRoot');
        return;
      }

      return this.contentRoot.querySelector('[data-step]')?.getAttribute('data-step');
    }

    get releaseCountdownTime() {
      const raw = this.dataset.releaseCountdownTime;
      if (!raw) return null;

      const time = new Date(raw).getTime();
      if (Number.isNaN(time)) {
        console.warn(`Invalid date: [data-release-countdown-time] "${raw}"`);
        return null;
      }

      return time;
    }

    get isReleased() {
      if (this.releaseCountdownTime === null) return false;

      return Date.now() >= this.releaseCountdownTime;
    }

    get hasAccessToVaultCollection() {
      // Access is a tag on the Shopify customer record, rendered server-side into
      // data-has-access-tag. It is deliberately NOT settable from the browser:
      // the previous localStorage flag could be granted from the dev console.
      return this.hasCustomerAccessTag;
    }

    openModalWindow() {
      document.body.appendChild(this.modalWindow);
      requestAnimationFrame(() => this.modalWindow.classList.add('is-open'));

      this.showInitialStep();

      if (typeof window.lenisInstance != 'undefined') {
        window.lenisInstance?.stop();
      } else {
        document.body.style.overflowY = 'hidden';
      }
    }

    closeModalWindow() {
      this.modalWindow.classList.remove('is-open');

      const onEnd = (event) => {
        if (event.target !== this.modalWindow || event.propertyName !== 'opacity') return;

        this.modalWindow.removeEventListener('transitionend', onEnd);
        this.modalWindow.remove();
        this.contentRoot.innerHTML = '';
      };

      this.modalWindow.addEventListener('transitionend', onEnd);

      if (typeof window.lenisInstance != 'undefined') {
        window.lenisInstance?.start();
      } else {
        document.body.style.overflowY = '';
      }
    }

    showInitialStep() {
      if (!this.isReleased && this.isEnableReleaseMode) {
        if (this.hasAccessToVaultCollection) {
          this.showStep('countdown-new-drop');
          return;
        }

        if (!this.hasAccessToVaultCollection) {
          this.showStep('collection-lock');
          return;
        }
      }

      if (this.isReleased || !this.isEnableReleaseMode) {
        if (this.hasAccessToVaultCollection) {
          this.showStep('collection-unlock');
          return;
        }

        if (!this.hasAccessToVaultCollection) {
          this.showStep('collection-lock');
        }
      }
    }

    showStep(name) {
      const step = this.steps[name];
      if (!step) {
        console.warn(`Not found: step "${name}"`);
        return;
      }

      this.resetForm(step.querySelector('form'));

      this.contentRoot.innerHTML = '';
      this.contentRoot.appendChild(step);
      step.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 300, easing: 'ease' }
      );
    }

    routePrevStep() {
      switch (this.currentStep) {
        case 'collection-lock':
          this.closeModalWindow();
          break;

        case 'collection-unlock':
          this.closeModalWindow();
          break;

        case 'email-subscribe':
          this.showStep('collection-lock');
          break;

        case 'private-access':
          this.showStep('email-subscribe');
          break;

        case 'countdown-new-drop':
          this.closeModalWindow();
          break;
      }
    }

    routeNextStep() {
      switch (this.currentStep) {
        case 'collection-lock':
          this.showStep('email-subscribe');
          break;

        case 'collection-unlock':
          break;

        case 'email-subscribe':
          this.showStep('private-access');
          break;

        case 'private-access':
          // The unlock tag is now on the Shopify customer record, but this page was
          // rendered before that happened. Reload so Liquid re-renders from the real
          // tag rather than the browser assuming access it cannot verify.
          window.location.reload();
          break;

        case 'countdown-new-drop':
          break;
      }
    }

    resetForm(form) {
      if (!form) return;

      form.reset();
      form.querySelectorAll('input[name]').forEach((input) => this.clearFieldError(form, input));
      form.querySelector('[data-role="message-holder"]')?.remove();
    }

    validateField(input) {
      const value = input.value.trim();

      if (input.required && !value) {
        return 'required';
      }

      if (!value) {
        return null;
      }

      if (input.type === 'email' && !this.isValidEmail(value)) {
        return 'invalid';
      }

      return null;
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    setFieldError(form, input, message) {
      input.classList.add('has-error');

      const errorEl = form.querySelector('[data-role="field-error"]');
      if (!errorEl) {
        console.warn('Not found: field-error');
        return;
      }

      const textEl = errorEl.querySelector('[data-role="field-error-text"]');
      if (textEl) textEl.textContent = message || '';

      errorEl.hidden = false;
    }

    clearFieldError(form, input) {
      input.classList.remove('has-error');

      const errorEl = form.querySelector('[data-role="field-error"]');
      if (errorEl) errorEl.hidden = true;
    }

    async sendSubscribeForm(form) {
      const url = 'https://backend-production-44745.up.railway.app/api/collection-unlock';

      const body = {};

      form.querySelectorAll('input[name]').forEach((input) => {
        body[input.name] = input.value.trim();
      });

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          redirect: 'follow'
        });

        const data = await response.json().catch(() => null);
        const ok = response.ok && data?.success === true;

        if (!ok) {
          console.warn(`Subscribe failed: status ${response.status}`, data?.message);
        }

        return {
          ok,
          status: response.status,
          message: data?.message
        };
      } catch (err) {
        console.error('Subscribe request error:', err);

        return {
          ok: false,
          status: 0,
          message: undefined
        };
      }
    }

    async handleSubscribeSubmit(form) {
      const inputs = form.querySelectorAll('input[name]');

      inputs.forEach((input) => this.clearFieldError(form, input));

      form.querySelector('[data-role="message-holder"]')?.remove();

      for (const input of inputs) {
        const errorType = this.validateField(input);
        if (!errorType) continue;

        const message = errorType === 'required'
          ? form.dataset.errorRequired
          : form.dataset.errorInvalid;

        this.setFieldError(form, input, message);
        return;
      }

      const submitButton = form.querySelector('[data-role="cta-submit"]');

      Shopify.Helpers.button.lock(submitButton);
      const result = await this.sendSubscribeForm(form);
      Shopify.Helpers.button.unlock(submitButton);

      if (!result.ok) {
        /*
         * Tell them what actually went wrong. A wrong code and a code someone
         * else already used need different responses from the customer — one is
         * "check what you typed", the other is "this is spent, ask us for
         * another". A single generic error would leave them retyping a code
         * that can never work.
         */
        const codeInput = form.querySelector('input[name="code"]');
        let message = form.dataset.errorRequest;

        if (result.status === 409) {
          message = form.dataset.errorUsed;
        } else if (result.status === 400) {
          message = form.dataset.errorInvalid;
        } else if (result.status === 429) {
          message = form.dataset.errorTooMany;
        }

        if (codeInput) {
          this.setFieldError(form, codeInput, message);
        } else {
          Shopify.Helpers.message(submitButton, {value: message, status: 'error'});
        }
        return;
      }

      // No client-side access flag is set. The unlock now lives only as a tag on the
      // customer record; this just advances to the reveal step, which reloads on exit.
      this.routeNextStep();
    }

    initStepTemplates() {
      const templatContainer = this.querySelector('[data-role="template-steps"]');
      if (!templatContainer) {
        console.error('Not found: [data-role="template-steps"]');
        return false;
      }

      const templates = templatContainer.content.cloneNode(true);
      templatContainer.remove();

      templates.querySelectorAll('[data-step]').forEach(elem => {
        this.steps[elem.dataset.step] = elem;
      });

      return Object.values(this.steps).some(item => Boolean(item));
    }

    initModalWindow() {
      const templateConainer = this.querySelector('[data-role="template-modal-window"]');
      if (!templateConainer) {
        console.error('Not found: [data-role="template-modal-window"]');
        return;
      }

      const template = templateConainer.content.cloneNode(true);
      templateConainer.remove();

      this.modalWindow = template.querySelector('[data-role="modal-window"]');
      this.contentContainer = template.querySelector('[data-role="modal-content-container"]');
      this.buttonPrevStep = template.querySelector('[data-role="modal-back"]');
      this.contentRoot = template.querySelector('[data-role="vault-collection-root"]');

      this.bindModalEvents();
    }

    initStepCollectionLock() {
      const step = this.steps['collection-lock'];
      if (!step) {
        console.warn('Not found: step "collection-lock"');
        return;
      }

      const swiperEl = step.querySelector('[data-role="swiper-cards-grid"]');
      if (swiperEl) {
        new Swiper(swiperEl, {
          observer: true,
          observeParents: true,
          slidesPerView: 2.1,
          spaceBetween: 8,
          breakpoints: {
            750: { slidesPerView: 3.2 },
            990: { slidesPerView: 4 }
          }
        });
      }

      const buttonTrigger = step.querySelector('[data-role="cta-button"]');
      if (!buttonTrigger) {
        console.warn('Not found: [data-role="cta-button"] in step "collection-lock"');
      }

      buttonTrigger?.addEventListener('click', this.routeNextStep.bind(this));
    }

    initStepCollectionUnlock() {
      const step = this.steps['collection-unlock'];
      if (!step) {
        console.warn('Not found: step "collection-unlock"');
        return;
      }

      this.initUnlockSwipers(step);
      this.initUnlockTabs(step);
    }

    initUnlockSwipers(step) {
      const swiperElems = step.querySelectorAll('[data-role="swiper-cards-grid"]');
      if (!swiperElems.length) {
        console.warn('Not found: unlock swiper-cards-grid');
        return;
      }

      swiperElems.forEach((swiperEl) => {
        if (swiperEl.swiper) return;

        new Swiper(swiperEl, {
          observer: true,
          observeParents: true,
          slidesPerView: 2.1,
          spaceBetween: 8,
          breakpoints: {
            750: { slidesPerView: 3.2 },
            990: { slidesPerView: 4 }
          }
        });
      });
    }

    initUnlockTabs(step) {
      const tabsContainer = step.querySelector('[data-role="tabs-container"]');
      if (!tabsContainer) {
        console.warn('Not found: unlock tabs-container');
        return;
      }

      const triggers = step.querySelectorAll('[data-role="tab-trigger"]');
      const contents = step.querySelectorAll('[data-role="tab-content"]');
      const ctaLink = step.querySelector('[data-role="cta-collection-link"]');
      if (!ctaLink) {
        console.warn('Not found: cta-collection-link');
      }

      tabsContainer.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-role="tab-trigger"]');
        if (!trigger) return;

        if (trigger.classList.contains('active')) return;

        const index = trigger.dataset.tabContentIndex;

        triggers.forEach((item) => item.classList.toggle('active', item === trigger));

        contents.forEach((content) => {
          const isActive = content.dataset.tabContentIndex === index;
          content.classList.toggle('active', isActive);

          if (!isActive) return;

          content.animate(
            [{ opacity: 0 }, { opacity: 1 }],
            { duration: 300, easing: 'ease' }
          );
        });

        if (ctaLink && trigger.dataset.collectionUrl) {
          ctaLink.href = trigger.dataset.collectionUrl;
        }
      });
    }

    initStepPrivateAccess() {
      const step = this.steps['private-access'];
      if (!step) {
        console.warn('Not found: step "private-access"');
        return;
      }

      const buttonTrigger = step.querySelector('[data-role="cta-next"]');
      if (!buttonTrigger) {
        console.warn('Not found: [data-role="cta-button"] in step "collection-lock"');
      }

      buttonTrigger?.addEventListener('click', this.routeNextStep.bind(this));
    }

    initStepSubscribe() {
      const step = this.steps['email-subscribe'];
      if (!step) {
        console.warn('Not found: step "email-subscribe"');
        return;
      }

      const form = step.querySelector('[data-role="subscribe-email-form"]');
      if (!form) {
        console.warn('Not found: subscribe-email-form');
        return;
      }

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        await this.handleSubscribeSubmit(form);
      });
    }

    initStepCountdown() {
      const step = this.steps['countdown-new-drop'];
      if (!step) {
        console.warn('Not found: step "countdown-new-drop"');
        return;
      }

      const countdown = step.querySelector('[data-role="countdown"]');
      if (!countdown) {
        console.warn('Not found: [data-role="countdown"]');
      }
    }

    initStepsConfig() {
      this.initStepCollectionLock();
      this.initStepCollectionUnlock();
      this.initStepSubscribe();
      this.initStepPrivateAccess();
      this.initStepCountdown();
    }

    bindOpenModal() {
      this.trigger.addEventListener('click', () => this.openModalWindow());
    }

    bindCloseModal() {
      this.modalWindow.addEventListener('click', (event) => {
        const target = event.target;

        if (!target.isConnected) return;

        if (target.closest('[data-role="modal-close"]')) {
          this.closeModalWindow();
          return;
        }

        if (!target.closest('[data-role="content-inner"]')) {
          this.closeModalWindow();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        if (!this.modalWindow.classList.contains('is-open')) return;

        this.closeModalWindow();
      });
    }

    bindPrevStep() {
      this.buttonPrevStep?.addEventListener('click', this.routePrevStep.bind(this));
    }

    bindModalEvents() {
      this.bindOpenModal();
      this.bindCloseModal();
      this.bindPrevStep();
    }

    init() {
      const isInitStepTemplates = this.initStepTemplates();
      if (!isInitStepTemplates) {
        console.error('Not found: steps in template');
        return;
      }

      this.initModalWindow();
      this.initStepsConfig();
    }
  }

  customElements.define('vault-modal', VaultModal);

  class VaultSection extends VaultModal {
    connectedCallback() {
      this.releaseCollectionData = this.getReleaseCollectionData();

      if (!this.releaseCollectionData) {
        console.warn('Not found: data-from-release-collection in localStorage');
        this.remove();
        return;
      }

      super.connectedCallback();
      this.setCollectionName();
      this.setCollectionPromoBanner();
      this.showInitialStep();
    }

    setCollectionName() {
      const { collectionName } = this.releaseCollectionData;

      if (!collectionName) {
        console.warn('Not found: collectionName in releaseCollectionData');
        return;
      }

      Object.values(this.steps).forEach((step) => {
        step?.querySelectorAll('[data-role="collection-name"]').forEach((elem) => {
          elem.textContent = collectionName;
        });
      });
    }

    setCollectionPromoBanner() {
      const { collectionPromoBanner, collectionName } = this.releaseCollectionData;

      Object.values(this.steps).forEach((step) => {
        step?.querySelectorAll('[data-role="collection-promo-banner"]').forEach((container) => {
          if (!collectionPromoBanner) {
            console.warn('Not found: collectionPromoBanner in releaseCollectionData');
            container.remove();
            return;
          }

          const image = document.createElement('img');
          image.src = collectionPromoBanner;
          image.alt = collectionName || '';
          image.loading = 'lazy';
          container.append(image);
        });
      });
    }

    getReleaseCollectionData() {
      const raw = localStorage.getItem('data-from-release-collection');
      if (!raw) return null;

      try {
        return JSON.parse(raw);
      } catch (err) {
        console.warn('Invalid JSON: data-from-release-collection', err);
        return null;
      }
    }

    get hasAccessToVaultCollection() {
      // Server-rendered value only — see the note on VaultModal.hasAccessToVaultCollection.
      return this.releaseCollectionData.hasAccess === 'true';
    }

    get releaseCountdownTime() {
      const raw = this.releaseCollectionData.dateRelease;
      if (!raw || raw === 'false') return null;

      const time = new Date(raw).getTime();
      if (Number.isNaN(time)) {
        console.warn(`Invalid date: dateRelease "${raw}"`);
        return null;
      }

      return time;
    }

    initModalWindow() {
      this.contentRoot = this.querySelector('[data-role="vault-collection-root"]');
      if (!this.contentRoot) {
        console.error('Not found: [data-role="vault-collection-root"]');
      }
    }

    initStepSubscribe() {
      super.initStepSubscribe();

      const form = this.steps['email-subscribe']?.querySelector('[data-role="subscribe-email-form"]');
      if (!form) return;

      // The API expects Shopify's global ID form, not a bare numeric ID.
      const collectionIdInput = form.querySelector('input[name="collection_id"]');
      if (collectionIdInput) {
        const raw = String(this.releaseCollectionData.collectionId || '');
        collectionIdInput.value = raw.startsWith('gid://')
          ? raw
          : `gid://shopify/Collection/${raw}`;
      }

      // Sending the title and URL means the nightly email job never has to ask
      // Shopify what this collection is called, so it needs no read_products scope.
      const titleInput = form.querySelector('input[name="collection_title"]');
      if (titleInput && this.releaseCollectionData.collectionName) {
        titleInput.value = this.releaseCollectionData.collectionName;
      }

      const urlInput = form.querySelector('input[name="collection_url"]');
      if (urlInput && this.releaseCollectionData.urlToReturnCollection) {
        urlInput.value = this.releaseCollectionData.urlToReturnCollection;
      }

      const dateInput = form.querySelector('input[name="date"]');
      if (dateInput) {
        dateInput.value = this.releaseCollectionData.dateRelease;
      }
    }

    initStepCountdown() {
      super.initStepCountdown();

      const countdown = this.steps['countdown-new-drop']?.querySelector('[data-role="countdown"]');
      if (!countdown) return;

      countdown.dataset.time = this.releaseCollectionData.dateRelease;
    }

    showInitialStep() {
      if (!this.hasAccessToVaultCollection) {
        this.showStep('collection-lock');
        return;
      }

      this.routeByReleaseState();
    }

    routeByReleaseState() {
      if (this.releaseCountdownTime !== null && !this.isReleased) {
        this.showStep('countdown-new-drop');
        return;
      }

      const returnUrl = this.releaseCollectionData.urlToReturnCollection;
      if (!returnUrl) {
        console.warn('Not found: urlToReturnCollection');
        return;
      }

      window.location.href = returnUrl;
    }

    routeNextStep() {
      if (this.currentStep !== 'private-access') {
        super.routeNextStep();
        return;
      }

      this.routeByReleaseState();
    }
  }

  customElements.define('vault-section', VaultSection);
}